import { useFormik } from 'formik';
import { useState } from 'react';
import { FiChevronLeft } from 'react-icons/fi';
import { useMutation } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';

import {
  icPeopleCheck,
  icTwoPeople,
  bgFriendCupCreate,
} from '@/assets/tournaments';
import { Button, Form, InputField, Modal } from '@/components';
import { Layout, Sidebar } from '@/components/GenericsTournaments';
import useFriendsCup from '@/hooks/useFriendsCup';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  requestCheckNameAvailability,
  requestNewTeam,
  getInitFCup,
} from '@/services/friendsCup';
import {
  createFCupSchema,
  oneSpaceBetweenWordMinTwoChar,
  TEXTS,
  validateFieldError,
} from '@/utils';

type TypeProps = {
  showModal: boolean;
  initDate: string | undefined;
};

const initialState = {
  showModal: false,
  initDate: undefined,
};

export const CreateTeamFCup = () => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();
  const navigate = useNavigate();
  const [state, setState] = useState<TypeProps>(initialState);
  const { groupData, handleSetGroup } = useFriendsCup();

  const { mutateAsync: saveNewTeam } = useMutation(
    async (formData: { groupName: string }) => {
      // First check if inscriptions are still open
      const currentStatus = await getInitFCup();
      if (!currentStatus?.estado || !currentStatus.inscripcionAbierta) {
        throw new Error('Finalizó la inscripción');
      }

      const { groupName } = formData;
      return requestNewTeam(groupName);
    }
  );

  const {
    touched,
    errors,
    isSubmitting,
    isValid,
    setFieldError,
    setFieldValue,
    handleSubmit,
    values,
    setFieldTouched,
    handleBlur,
    setStatus,
    status,
  } = useFormik({
    validateOnMount: false,
    validateOnBlur: true,
    validationSchema: createFCupSchema,
    initialValues: {
      groupName: '',
    },
    onSubmit: async (
      {
        groupName,
      }: {
        groupName: string;
      },
      { setSubmitting }
    ) => {
      if (groupName) {
        try {
          const response = await saveNewTeam({ groupName });

          if (response.estado) {
            handleSetGroup({
              idGrupo: response.idGrupo ?? 0,
              nombre: response.nombre ?? '',
              estado: response.estado,
              txtFechaReemplazar: response.txtFechaReemplazar,
            });
            setState({
              showModal: true,
              initDate: response?.txtFechaReemplazar,
            });
          } else {
            toast.show(response.mensaje);
          }

          setSubmitting(false);
        } catch (error) {
          if (
            error instanceof Error &&
            error.message === 'Finalizó la inscripción'
          ) {
            toast.show('Finalizó la inscripción');
          } else {
            console.error(error);
            toast.show(TEXTS.defaultError);
          }
          setSubmitting(false);
        }
      }
    },
  });

  const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;
  const handleNameTournamentValidation = async (groupName: string) => {
    if (groupName.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
      const response = await requestCheckNameAvailability(
        encodeURIComponent(groupName)
      );
      if (!response.estado && 'codError' in response) {
        if (response.codError === 1603) {
          setFieldError('groupName', TEXTS.nameExist);
          setStatus(false);
        } else {
          setFieldError('groupName', response.mensaje || TEXTS.defaultError);
          setStatus(false);
        }
      } else {
        setFieldError('groupName', undefined);
        setStatus(true);
      }
    } else {
      setStatus(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

    setFieldValue('groupName', value);
    setFieldTouched('groupName', true, false);

    if (value.trim().length === 0) {
      setFieldError('groupName', TEXTS.minLetters);
      setStatus(false);
    } else {
      handleNameTournamentValidation(value);
    }
  };

  return (
    <Layout
      actionFooter={{
        actionHandler: handleSubmit,
        buttonName: 'Crear equipo',
        buttonDisabled:
          isSubmitting || !isValid || values.groupName?.length === 0,
      }}
      className="px-4 lg:w-[974px] lg:mt-6"
      main={() => (
        <div className="lg:w-[400px] lg:min-h-[670px]">
          {isDesktop && (
            <>
              <Link
                className="text-gdt-blue font-bold relative left-[-15px]"
                to={ROUTES.friendsCup.home}
              >
                <FiChevronLeft className="text-gdt-blue w-6 h-6 inline" />
                <span className="pl-[4px]">Volver</span>
              </Link>
              <div className="mt-[40px]">
                <span className="font-bold text-base text-gdt-gray lg:text-lg">
                  Crear nuevo equipo
                </span>
              </div>
            </>
          )}
          <p className="font-medium lg:font-semibold text-sm mt-4 text-gdt-gray-2">
            Creá tu equipo y competí frente a los demás equipos a partir de la{' '}
            {groupData.txtFechaReemplazar}
            {'.'}
          </p>
          <div className="lg:w-[80%] mt-5 lg:mt-8">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.groupName,
                  errors.groupName,
                  values.groupName.length
                )}
                label="Nombre del equipo"
                maxLength={25}
                name="groupName"
                placeholder="Ingresar nombre"
                success={
                  !errors.groupName && values.groupName?.length > 0 && status
                }
                value={values.groupName}
                onBlur={(e: React.FocusEvent<any, Element>) => {
                  if (values.groupName.length <= 2) {
                    handleBlur(e);
                  }
                }}
                onChange={handleInputChange}
              />
            </Form.Field>
          </div>
          {isDesktop && (
            <div className="w-52 mt-10">
              <Button
                className="w-full"
                disabled={
                  isSubmitting || !isValid || values.groupName?.length === 0
                }
                type="button"
                variant="primary"
                onClick={handleSubmit}
              >
                Crear equipo
              </Button>
            </div>
          )}

          <Modal
            bottomSheetClassName="px-[16px] pt-[20px] pb-[40px]"
            className="px-4 lg:px-11 py-10 min-w-[550px]"
            closeOutside={false}
            controlledOnClose={() => null}
            isControlledOpen={state.showModal}
            render={() => {
              return (
                <div>
                  <p className="text-base font-semibold mb-[30px] text-center">
                    ¡Felicitaciones! Tu equipo fue creado con éxito.
                    <br />
                    Ahora invitá a 3 amigos y competí desde la {state.initDate}.
                    <br />
                    Recordá que suman los puntos de tu 11 y el de tus 3 amigos.
                  </p>
                  <div className="flex space-x-[10px] h-[50px]">
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.friendsCup.home);
                      }}
                    >
                      Invitar más tarde
                    </Button>
                    <Button
                      className="flex-1 px-0"
                      type="button"
                      variant="primary"
                      onClick={() => {
                        setState(initialState);
                        navigate(ROUTES.friendsCup.invite);
                      }}
                    >
                      Aceptar
                    </Button>
                  </div>
                </div>
              );
            }}
            showCloseButton={false}
            size="xs"
            variant="controlled"
          />
        </div>
      )}
      mobile={{
        leftContent: {
          backAction: () => {
            navigate(ROUTES.friendsCup.home);
          },
        },
        centerContent: { title: 'Crear equipo' },
      }}
      sidebar={() =>
        isDesktop && (
          <Sidebar className="lg:w-[426px]">
            <div
              className="relative overflow-hidden rounded-md bg-no-repeat bg-left h-[280px] bg-cover"
              style={{
                backgroundImage: `url(${bgFriendCupCreate})`,
              }}
            >
              <div className="px-14 relative">
                <p className="text-[26px] font-normal font-bebas text-white mt-[70px]">
                  COMO ADMINISTRADOR DE UN EQUIPO:
                </p>
                <ul className="space-y-7 mt-9">
                  <li className="flex items-center">
                    <figure className="w-7 h-7 bg-white/30 rounded-full flex items-center justify-center">
                      <img className="inline" src={icTwoPeople} />
                    </figure>
                    <span className="text-white ml-[5px]">
                      Invitá a quien quieras.
                    </span>
                  </li>
                  <li className="flex items-center">
                    <figure className="w-7 h-7 bg-white/30 rounded-full flex items-center justify-center">
                      <img className="inline" src={icPeopleCheck} />
                    </figure>
                    <span className="text-white ml-[5px]">
                      Aceptá a quien se postule en tu equipo.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </Sidebar>
        )
      }
      withBanners={false}
    />
  );
};
